import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { loadGoogleMapsForVenuePicker } from "../load-google-maps";
import {
  MIQAAT_VENUE_MAPS_DEFAULT_CENTER,
  MIQAAT_VENUE_MAPS_DEFAULT_ZOOM,
  MIQAAT_VENUE_MAPS_FIT_PADDING_PX,
  buildVenueCoordsFromPin,
  parseVenueLatLng,
  resolveGoogleMapsApiKey,
  resolveRadiusCircleMeters,
  resolveVenueMapFitRadiusMeters,
  venueMapFitBoundsLiteral,
} from "../miqaat-venue-location";
import MiqaatVenuePlaceSearchField from "./MiqaatVenuePlaceSearchField";

/**
 * Google Maps Place Autocomplete (New) + optional map pin adjust mode.
 * Writes `name` / `latitude` / `longitude` into the react-admin form.
 *
 * Map pan/zoom is view-only by default. Pin placement is a separate UX:
 * enter “Adjust pin on map”, then click or drag; Done exits.
 */
export default function MiqaatVenueLocationPicker() {
  const theme = useTheme();
  const { setValue } = useFormContext();
  const latitude = useWatch({ name: "latitude" });
  const longitude = useWatch({ name: "longitude" });
  const radiusMeters = useWatch({ name: "radiusMeters" });

  const apiKey = resolveGoogleMapsApiKey();
  const mapHostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const mapClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const dragStartListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const dragEndListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    apiKey ? "loading" : "idle"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pinAdjustMode, setPinAdjustMode] = useState(false);

  const writePin = useCallback(
    (lat: number, lng: number) => {
      const coords = buildVenueCoordsFromPin({ latitude: lat, longitude: lng });
      setValue("latitude", coords.latitude, { shouldDirty: true, shouldValidate: true });
      setValue("longitude", coords.longitude, { shouldDirty: true, shouldValidate: true });
      markerRef.current?.setPosition({ lat: coords.latitude, lng: coords.longitude });
      circleRef.current?.setCenter({ lat: coords.latitude, lng: coords.longitude });
    },
    [setValue]
  );

  // Init map once when the API key is present (view-only pin until adjust mode).
  useEffect(() => {
    if (!apiKey || !mapHostRef.current) {
      return;
    }

    let cancelled = false;
    const mapHost = mapHostRef.current;
    const circleStroke = theme.palette.primary.main;
    const circleFill = theme.palette.primary.main;

    void (async () => {
      try {
        setStatus("loading");
        const { Map, Marker, Circle } = await loadGoogleMapsForVenuePicker(apiKey);
        if (cancelled) {
          return;
        }

        const initial = parseVenueLatLng(latitude, longitude) ?? MIQAAT_VENUE_MAPS_DEFAULT_CENTER;

        const map = new Map(mapHost, {
          center: initial,
          zoom: MIQAAT_VENUE_MAPS_DEFAULT_ZOOM,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: "greedy",
        });
        mapRef.current = map;

        // optimized:false keeps a real DOM marker so drag hit-testing works reliably.
        const marker = new Marker({
          map,
          position: initial,
          draggable: false,
          optimized: false,
          zIndex: 1_000_000,
          title: "Check-in point",
        });
        markerRef.current = marker;

        const initialRadius = resolveRadiusCircleMeters(radiusMeters);
        const circle = new Circle({
          map,
          center: initial,
          radius: initialRadius ?? 0,
          clickable: false,
          zIndex: 1,
          strokeColor: circleStroke,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: circleFill,
          fillOpacity: 0.12,
          visible: initialRadius != null,
        });
        circleRef.current = circle;

        if (!cancelled) {
          setStatus("ready");
          setErrorMessage(null);
        }
      } catch (err) {
        if (!cancelled) {
          setStatus("error");
          setErrorMessage(err instanceof Error ? err.message : "Failed to load Google Maps.");
        }
      }
    })();

    return () => {
      cancelled = true;
      mapClickListenerRef.current?.remove();
      mapClickListenerRef.current = null;
      dragStartListenerRef.current?.remove();
      dragStartListenerRef.current = null;
      dragEndListenerRef.current?.remove();
      dragEndListenerRef.current = null;
      markerRef.current?.setMap(null);
      circleRef.current?.setMap(null);
      markerRef.current = null;
      circleRef.current = null;
      mapRef.current = null;
    };
    // Init once per API key / mount — form sync is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot map bootstrap
  }, [apiKey]);

  // Toggle click-to-place + drag only while adjusting the pin.
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    const circle = circleRef.current;
    if (!map || !marker || status !== "ready") {
      return;
    }

    mapClickListenerRef.current?.remove();
    mapClickListenerRef.current = null;
    dragStartListenerRef.current?.remove();
    dragStartListenerRef.current = null;
    dragEndListenerRef.current?.remove();
    dragEndListenerRef.current = null;

    if (!pinAdjustMode) {
      marker.setOptions({ draggable: false, title: "Check-in point" });
      map.setOptions({ draggable: true, gestureHandling: "greedy" });
      // Restore radius overlay after adjust.
      const radius = resolveRadiusCircleMeters(radiusMeters);
      if (circle) {
        circle.setVisible(radius != null);
      }
      return;
    }

    // Circle fill can steal pointer events from the pin — hide while adjusting.
    circle?.setVisible(false);

    marker.setOptions({
      draggable: true,
      optimized: false,
      clickable: true,
      zIndex: 1_000_000,
      title: "Drag to the check-in spot, or click the map",
    });

    mapClickListenerRef.current = map.addListener("click", (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) {
        return;
      }
      writePin(event.latLng.lat(), event.latLng.lng());
    });

    // Prevent the map from panning away while the user drags the pin.
    dragStartListenerRef.current = marker.addListener("dragstart", () => {
      map.setOptions({ draggable: false, gestureHandling: "none" });
    });
    dragEndListenerRef.current = marker.addListener("dragend", () => {
      map.setOptions({ draggable: true, gestureHandling: "greedy" });
      const pos = marker.getPosition();
      if (!pos) {
        return;
      }
      writePin(pos.lat(), pos.lng());
    });

    return () => {
      mapClickListenerRef.current?.remove();
      mapClickListenerRef.current = null;
      dragStartListenerRef.current?.remove();
      dragStartListenerRef.current = null;
      dragEndListenerRef.current?.remove();
      dragEndListenerRef.current = null;
      map.setOptions({ draggable: true, gestureHandling: "greedy" });
    };
  }, [pinAdjustMode, status, writePin, radiusMeters]);

  // Keep pin + circle in sync when form values change (edit load, place search, manual lat/lng, radius).
  useEffect(() => {
    const marker = markerRef.current;
    const map = mapRef.current;
    const circle = circleRef.current;
    if (!marker || !map || !circle) {
      return;
    }
    const coords = parseVenueLatLng(latitude, longitude);
    if (coords) {
      const current = marker.getPosition();
      const moved =
        !current ||
        Math.abs(current.lat() - coords.lat) > 1e-7 ||
        Math.abs(current.lng() - coords.lng) > 1e-7;
      if (moved) {
        marker.setPosition(coords);
        circle.setCenter(coords);
        map.panTo(coords);
      }
    }
    const radius = resolveRadiusCircleMeters(radiusMeters);
    // Don't show the radius overlay while placing the pin (blocks drag hit-testing).
    if (pinAdjustMode || radius == null) {
      circle.setVisible(false);
    } else {
      circle.setRadius(radius);
      circle.setVisible(true);
    }
  }, [latitude, longitude, radiusMeters, pinAdjustMode]);

  if (!apiKey) {
    return (
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        Set <code>VITE_GOOGLE_MAPS_API_KEY</code> (Maps JavaScript API + Places API New) to use
        place search and the map pin. You can still enter latitude/longitude manually below.
      </Alert>
    );
  }

  const applyPlaceFields = (fields: { latitude: number; longitude: number; name?: string }) => {
    if (fields.name !== undefined) {
      setValue("name", fields.name, { shouldDirty: true, shouldValidate: true });
    }
    setValue("latitude", fields.latitude, { shouldDirty: true, shouldValidate: true });
    setValue("longitude", fields.longitude, { shouldDirty: true, shouldValidate: true });
    const pos = { lat: fields.latitude, lng: fields.longitude };
    markerRef.current?.setPosition(pos);
    circleRef.current?.setCenter(pos);

    const map = mapRef.current;
    if (map) {
      const fitRadius = resolveVenueMapFitRadiusMeters(radiusMeters);
      map.fitBounds(venueMapFitBoundsLiteral(pos, fitRadius), MIQAAT_VENUE_MAPS_FIT_PADDING_PX);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Search for a place to set the approximate location. Pan and zoom the map freely; use Adjust
        pin only when you need to place the check-in spot. Radius draws an optional circle.
      </Typography>
      <MiqaatVenuePlaceSearchField apiKey={apiKey} onPlaceResolved={applyPlaceFields} />
      {status === "error" && errorMessage && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {errorMessage}
        </Alert>
      )}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1, mb: 1 }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Map preview
        </Typography>
        {pinAdjustMode ? (
          <Button size="small" variant="contained" onClick={() => setPinAdjustMode(false)}>
            Done adjusting pin
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            disabled={status !== "ready"}
            onClick={() => setPinAdjustMode(true)}
          >
            Adjust pin on map
          </Button>
        )}
      </Stack>
      {pinAdjustMode && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Click the map or drag the pin to set the check-in point. The radius circle is hidden while
          adjusting so the pin can be grabbed. Finish with Done when it looks right.
        </Alert>
      )}
      {/* Outer chrome only — keep the map host styles stable so Maps hit-testing stays valid. */}
      <Box
        sx={{
          borderRadius: 1,
          border: 2,
          borderColor: pinAdjustMode ? "primary.main" : "divider",
          borderStyle: pinAdjustMode ? "dashed" : "solid",
          overflow: "hidden",
        }}
      >
        <Box
          ref={mapHostRef}
          sx={(t) => ({
            width: "100%",
            height: t.spacing(40),
            bgcolor: alpha(t.palette.action.hover, 0.4),
          })}
        />
      </Box>
      {status === "loading" && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Loading map…
        </Typography>
      )}
    </Box>
  );
}
